import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  CreditCard,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  Save,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import Headerdash from "../pages/head";
import Sidebar from "../pages/sidebar";

const avatarFallback =
  "https://www.shutterstock.com/image-vector/user-profile-icon-vector-avatar-600nw-2220431045.jpg";

const AccountShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const Field = ({ id, label, icon: Icon, className = "", ...props }) => (
  <div className={className}>
    <label
      htmlFor={id}
      className="block text-xs font-semibold uppercase text-gray-500"
    >
      {label}
    </label>
    <div className="relative mt-2">
      {Icon && (
        <Icon
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          aria-hidden="true"
        />
      )}
      <input
        id={id}
        className={`w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-gray-800 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 ${
          Icon ? "pl-10" : ""
        }`}
        {...props}
      />
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, eyebrow, title, helper }) => (
  <div className="mb-5 flex items-start gap-3">
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
    <div>
      <p className="text-sm font-semibold uppercase text-purple-700">
        {eyebrow}
      </p>
      <h2 className="text-xl font-extrabold text-gray-900">{title}</h2>
      {helper && <p className="mt-1 text-sm text-gray-500">{helper}</p>}
    </div>
  </div>
);

export default function AccountPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [profilePic, setProfilePic] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    pinCode: "",
    country: "",
    currentPassword: "",
    newPassword: "",
    currentPlan: "Basic Plan",
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [accountDeletionEnabled, setAccountDeletionEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    const loadControls = async () => {
      try {
        const response = await fetch("/api/account-controls", { credentials: "include", cache: "no-store" });
        const data = response.ok ? await response.json() : null;
        if (active) setAccountDeletionEnabled(data?.accountDeletionEnabled === true);
      } catch {
        if (active) setAccountDeletionEnabled(false);
      }
    };
    loadControls();
    window.addEventListener("focus", loadControls);
    return () => {
      active = false;
      window.removeEventListener("focus", loadControls);
    };
  }, []);

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const [userResponse, subscriptionResponse] = await Promise.all([
          fetch("/api/user-info", {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }),
          fetch("/api/current-subscription", {
            method: "GET",
            credentials: "include",
          }),
        ]);

        if (userResponse.ok) {
          const data = await userResponse.json();
          setFormData((current) => ({
            ...current,
            firstName: data.firstname || "",
            lastName: data.lastname || "",
            email: data.email || data.username || "",
          }));
        }

        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          setFormData((current) => ({
            ...current,
            currentPlan: subscriptionData.currentPlan || current.currentPlan,
          }));
        }
      } catch (error) {
        console.error("Error fetching account data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccountData();
  }, []);

  useEffect(() => {
    return () => {
      if (profilePic) {
        URL.revokeObjectURL(profilePic);
      }
    };
  }, [profilePic]);

  const displayName = useMemo(() => {
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    return fullName || formData.email || "Student";
  }, [formData.email, formData.firstName, formData.lastName]);

  const initials = useMemo(() => {
    const parts = displayName.split(" ").filter(Boolean);
    return parts
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  }, [displayName]);

  const handleChange = (field) => (event) => {
    setFormData((current) => ({
      ...current,
      [field]: event.target.value,
    }));
    setStatusMessage("");
  };

  const handleProfilePicChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (profilePic) {
      URL.revokeObjectURL(profilePic);
    }

    setProfilePic(URL.createObjectURL(file));
    setStatusMessage("");
  };

  const handleReset = () => {
    setFormData((current) => ({
      ...current,
      currentPassword: "",
      newPassword: "",
    }));
    setStatusMessage("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatusMessage("Account changes are ready to save.");
  };

  return (
    <AccountShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Account
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            Manage your profile
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Keep your personal details, login credentials, and subscription
            preferences up to date.
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3 shadow-sm">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase text-gray-500">
              Account status
            </p>
            <p className="text-sm font-bold text-gray-900">Secure workspace</p>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="flex min-h-[360px] items-center justify-center rounded-md border border-slate-200 bg-white p-6 text-sm font-semibold text-gray-500 shadow-sm">
          <Loader2 className="mr-2 h-4 w-4 animate-spin text-purple-700" />
          Loading account details
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 lg:grid-cols-[minmax(280px,360px),1fr]">
            <aside className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col items-center text-center">
                <div className="relative">
                  {profilePic ? (
                    <img
                      src={profilePic}
                      alt=""
                      className="h-28 w-28 rounded-full border border-slate-200 object-cover shadow-sm"
                    />
                  ) : (
                    <div className="grid h-28 w-28 place-items-center rounded-full border border-slate-200 bg-purple-50 text-3xl font-extrabold text-purple-700 shadow-sm">
                      {initials || (
                        <img
                          src={avatarFallback}
                          alt=""
                          className="h-full w-full rounded-full object-cover"
                        />
                      )}
                    </div>
                  )}

                  <label
                    htmlFor="upload-picture"
                    className="absolute bottom-1 right-1 grid h-9 w-9 cursor-pointer place-items-center rounded-md bg-violet-600 text-white shadow-sm transition hover:bg-violet-700 focus-within:ring-2 focus-within:ring-violet-500 focus-within:ring-offset-2"
                    aria-label="Upload profile picture"
                  >
                    <Camera className="h-4 w-4" aria-hidden="true" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePicChange}
                      id="upload-picture"
                      className="sr-only"
                    />
                  </label>
                </div>

                <h2 className="mt-4 text-xl font-extrabold text-gray-900">
                  {displayName}
                </h2>
                <p className="mt-1 max-w-full truncate text-sm text-gray-500">
                  {formData.email || "No email added"}
                </p>
              </div>

              <div className="mt-6 rounded-md bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase text-gray-500">
                  Current plan
                </p>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
                      <CreditCard className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="truncate text-sm font-bold text-gray-900">
                      {formData.currentPlan}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/billing")}
                    className="shrink-0 rounded-md bg-white px-3 py-2 text-xs font-bold text-purple-700 shadow-sm ring-1 ring-purple-100 transition hover:bg-purple-50"
                  >
                    Change
                  </button>
                </div>
              </div>
            </aside>

            <div className="space-y-6">
              <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <SectionHeader
                  icon={UserRound}
                  eyebrow="Profile"
                  title="Personal information"
                  helper="This information helps personalize your PrepNexis workspace."
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <Field
                    id="first-name"
                    label="First name"
                    icon={UserRound}
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange("firstName")}
                    placeholder="First name"
                  />
                  <Field
                    id="last-name"
                    label="Last name"
                    icon={UserRound}
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange("lastName")}
                    placeholder="Last name"
                  />
                  <Field
                    id="email"
                    label="Email"
                    icon={Mail}
                    type="email"
                    value={formData.email}
                    onChange={handleChange("email")}
                    placeholder="name@example.com"
                    className="md:col-span-2"
                  />
                </div>
              </section>

              <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <SectionHeader
                  icon={KeyRound}
                  eyebrow="Security"
                  title="Password"
                  helper="Use a strong password to protect your study progress."
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Field
                      id="current-password"
                      label="Current password"
                      icon={KeyRound}
                      type="password"
                      value={formData.currentPassword}
                      onChange={handleChange("currentPassword")}
                      placeholder="Enter current password"
                    />
                    <a
                      href="/forgotpassword"
                      className="mt-2 inline-flex text-sm font-semibold text-purple-700 transition hover:text-purple-900"
                    >
                      Forgot password?
                    </a>
                  </div>
                  <Field
                    id="new-password"
                    label="New password"
                    icon={KeyRound}
                    type="password"
                    value={formData.newPassword}
                    onChange={handleChange("newPassword")}
                    placeholder="Enter new password"
                  />
                </div>
              </section>

              <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <SectionHeader
                  icon={MapPin}
                  eyebrow="Location"
                  title="Address details"
                  helper="Optional details for billing and account records."
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <Field
                    id="address"
                    label="Address"
                    icon={MapPin}
                    type="text"
                    value={formData.address}
                    onChange={handleChange("address")}
                    placeholder="Enter your address"
                    className="md:col-span-2"
                  />
                  <Field
                    id="pin-code"
                    label="Pin code"
                    type="text"
                    value={formData.pinCode}
                    onChange={handleChange("pinCode")}
                    placeholder="Enter pin code"
                  />
                  <Field
                    id="country"
                    label="Country"
                    type="text"
                    value={formData.country}
                    onChange={handleChange("country")}
                    placeholder="Enter country"
                  />
                </div>
              </section>
            </div>
          </div>

          <section className="mt-6 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-red-50 text-red-600">
                  <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase text-gray-500">
                    Account controls
                  </p>
                  <p className="text-sm text-gray-500">
                    Save profile edits or clear unsaved password changes.
                  </p>
                  {statusMessage && (
                    <p className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-green-700">
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                      {statusMessage}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {accountDeletionEnabled && (
                  <button
                    type="button"
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-bold text-red-700 shadow-sm ring-1 ring-red-100 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    Delete account
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                >
                  <Save className="h-4 w-4" aria-hidden="true" />
                  Save changes
                </button>
              </div>
            </div>
          </section>
        </form>
      )}
    </AccountShell>
  );
}
